import { useParams } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import { ArtData } from '../utils/ArtData';
import { Routes } from '../utils/Routes';
import LayOut from '../components/common/Layout';
import { PricingInfo } from '../components/common/PricingInfo';
import ArtPlaceholder from '../assets/images/ArtPlaceholder.svg';
import MusicCard from '../components/unspokenMusic/MusicCard';
import musicMetadata from '../assets/music/metadata.json';
import NFTIcon from '../assets/images/nft_icon.png';
import BackToGalleryLink from '../components/common/BackToGalleryLink';
import NotFound from './NotFound';

export default function ArtDetails() {
  const { id } = useParams<{ id: string }>();
  const preloadedImages = useRef<Set<string>>(new Set());

  const artCollection = ArtData.find(
    (art) => art.metadata.id?.toLowerCase() === id?.toLowerCase(),
  );

  const musicMeta = musicMetadata.find(
    (music) => music.filename === artCollection?.metadata.music,
  );

  const musicTitle = musicMeta?.title || 'Untitled Soundtrack';
  const musicArtist = musicMeta?.artist || 'Unknown Artist';

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [loadedImages, setLoadedImages] = useState<boolean[]>(() =>
    artCollection?.images
      ? new Array(artCollection.images.length).fill(false)
      : [],
  );

  const [isFullScreen, setIsFullScreen] = useState(false);

  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const [containerHeight, setContainerHeight] = useState<number>(0);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (artCollection?.images) {
      setLoadedImages(new Array(artCollection.images.length).fill(false));
      setCurrentImageIndex(0);
    }
  }, [artCollection?.images]);

  const preloadImage = useCallback((src: string) => {
    if (!preloadedImages.current.has(src)) {
      const img = new Image();
      img.src = src;
      preloadedImages.current.add(src);
    }
  }, []);

  useEffect(() => {
    if (artCollection?.images) {
      const imagesToPreload = [
        artCollection.images[currentImageIndex],
        artCollection.images[currentImageIndex + 1],
        artCollection.images[currentImageIndex - 1],
      ].filter(Boolean);
      imagesToPreload.forEach(preloadImage);
    }
  }, [currentImageIndex, artCollection?.images, preloadImage]);

  useEffect(() => {
    if (isFullScreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isFullScreen]);

  useEffect(() => {
    if (imageContainerRef.current && loadedImages[currentImageIndex]) {
      const currentHeight = imageContainerRef.current.offsetHeight;
      if (currentHeight > containerHeight) {
        setContainerHeight(currentHeight);
      }
    }
  }, [currentImageIndex, loadedImages, containerHeight]);

  if (
    !artCollection ||
    !artCollection.images ||
    artCollection.images.length === 0
  ) {
    return <NotFound />;
  }

  const { images, metadata } = artCollection;

  const handleNext = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handlePrevious = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleImageLoad = (index: number) => {
    setLoadedImages((prev) => {
      const updated = [...prev];
      updated[index] = true;
      return updated;
    });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) handleNext();
    else if (distance < -50) handlePrevious();
    setTouchStart(null);
    setTouchEnd(null);
  };

  const handleFullScreenToggle = () => setIsFullScreen((prev) => !prev);

  const metaFields = [
    { label: 'Artist', value: metadata.artist },
    { label: 'Medium', value: metadata.medium },
    { label: 'Year', value: metadata.year },
    metadata.dimensions?.width && metadata.dimensions?.height
      ? {
          label: 'Dimensions',
          value: `${metadata.dimensions.width} × ${metadata.dimensions.height}`,
        }
      : null,
    { label: 'Style', value: metadata.style },
  ].filter((f): f is { label: string; value: string } => !!f && !!f.value);

  return (
    <LayOut layoutStyle="bg-white">
      <section className="text-left">
        <div className="max-w-6xl mx-auto px-4 py-6 md:py-10">
          {/* Back link */}
          <div className="mb-10 mt-16 md:mt-6">
            <BackToGalleryLink to={`${Routes.Art}?id=${metadata.id}`} />
          </div>

          <div className="flex flex-col lg:flex-row lg:gap-14 items-center">
            {/* Carousel */}
            <div
              className="w-full lg:w-1/2 lg:max-w-md"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* Mobile title */}
              <h1 className="lg:hidden text-base font-light tracking-[0.15em] uppercase text-custom-coffee mb-6">
                {metadata.title || 'Untitled'}
              </h1>

              {/* Image */}
              <div
                ref={imageContainerRef}
                className="relative cursor-pointer overflow-hidden"
                style={{
                  minHeight: containerHeight > 0 ? containerHeight : 'auto',
                }}
                onClick={handleFullScreenToggle}
              >
                {!loadedImages[currentImageIndex] && (
                  <img
                    src={ArtPlaceholder}
                    alt="Loading"
                    className="w-full h-auto block"
                  />
                )}
                <img
                  src={images[currentImageIndex]}
                  alt={`${metadata.title} — ${currentImageIndex + 1}`}
                  className={`w-full h-auto block transition-opacity duration-500 ${
                    loadedImages[currentImageIndex]
                      ? 'opacity-100'
                      : 'opacity-0'
                  } ${!loadedImages[currentImageIndex] ? 'absolute inset-0' : ''}`}
                  onLoad={() => handleImageLoad(currentImageIndex)}
                />
              </div>

              {/* Carousel navigation */}
              {images.length > 1 && (
                <div className="flex items-center justify-center mt-5 gap-5">
                  <button
                    onClick={handlePrevious}
                    aria-label="Previous image"
                    className="text-gray-400 hover:text-gray-700 transition-colors"
                  >
                    <svg
                      width="18"
                      height="18"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path
                        d="M12 15l-5-5 5-5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  <div className="flex gap-2">
                    {images.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentImageIndex(i)}
                        aria-label={`Image ${i + 1}`}
                        className={`w-1.5 h-1.5 rounded-full transition-colors duration-200 ${
                          i === currentImageIndex
                            ? 'bg-gray-600'
                            : 'bg-gray-200 hover:bg-gray-400'
                        }`}
                      />
                    ))}
                  </div>

                  <button
                    onClick={handleNext}
                    aria-label="Next image"
                    className="text-gray-400 hover:text-gray-700 transition-colors"
                  >
                    <svg
                      width="18"
                      height="18"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path
                        d="M6 5l5 5-5 5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            {/* Info panel */}
            <div className="w-full lg:w-1/2 mt-10 lg:mt-0">
              {/* Desktop title */}
              <h1 className="hidden lg:block text-lg font-light tracking-[0.15em] uppercase text-custom-coffee pb-6 mb-6 border-b border-gray-200">
                {metadata.title || 'Untitled'}
              </h1>

              {/* Meta fields */}
              {metaFields.length > 0 && (
                <dl className="space-y-3 mb-8">
                  {metaFields.map((field) => (
                    <div key={field.label} className="flex gap-6">
                      <dt className="w-24 text-gray-400 text-[10px] uppercase tracking-widest pt-0.5 shrink-0">
                        {field.label}
                      </dt>
                      <dd className="text-gray-700 text-sm font-light">
                        {field.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}

              {/* Description */}
              {metadata.description && (
                <p className="text-gray-500 text-sm leading-relaxed italic border-l border-gray-300 pl-4 mb-8">
                  {metadata.description}
                </p>
              )}

              {/* Music */}
              {metadata.music && musicMeta && (
                <div className="border-t border-gray-200 pt-6 mb-6">
                  <MusicCard
                    musicFileName={musicMeta.filename}
                    title={musicTitle}
                    artist={musicArtist}
                  />
                </div>
              )}

              {/* Pricing */}
              <div className="border-t border-gray-200 pt-6">
                <PricingInfo metadata={metadata} />
              </div>

              {/* NFT */}
              {metadata.nft && (
                <div className="mt-6">
                  <a
                    href={metadata.nft}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block group"
                  >
                    <img
                      src={NFTIcon}
                      alt="View NFT"
                      className="w-10 h-10 transition-transform group-hover:scale-110 opacity-60 hover:opacity-100"
                    />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Full-screen overlay */}
      {isFullScreen && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex justify-center items-center"
          onClick={handleFullScreenToggle}
        >
          <img
            src={images[currentImageIndex]}
            alt={metadata.title}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePrevious();
            }}
            aria-label="Previous"
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors p-3"
          >
            <svg
              width="28"
              height="28"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path
                d="M18 22l-8-9 8-9"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            aria-label="Next"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors p-3"
          >
            <svg
              width="28"
              height="28"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path
                d="M10 22l8-9-8-9"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleFullScreenToggle();
            }}
            aria-label="Close"
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors p-2"
          >
            <svg
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M4 4l12 12M16 4L4 16" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}
    </LayOut>
  );
}
